import { adminGraphqlProxy } from "../helper/Common";

const GRAPHQL_TIMEOUT_MS = 60000;

function extractErrorMessages(rawErrors) {
  if (!rawErrors) return [];
  if (Array.isArray(rawErrors)) {
    return rawErrors
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e === "object") return e.message || JSON.stringify(e);
        return "";
      })
      .filter(Boolean);
  }
  if (typeof rawErrors === "string") return [rawErrors];
  if (typeof rawErrors === "object") {
    if (typeof rawErrors.message === "string") return [rawErrors.message];
    return [JSON.stringify(rawErrors)];
  }
  return [String(rawErrors)];
}

function throwIfGraphqlErrors(response, prefix) {
  const messages = extractErrorMessages(response?.errors);
  if (messages.length > 0) {
    throw new Error(`${prefix}: ${messages.join(", ")}`);
  }
}

/**
 * Poll MediaImage until READY (same flow as Admin_react shopifyGraphQL.js).
 */
async function pollMediaImageUrl(mediaImageId, options = {}) {
  const pollIntervalMs = options.pollIntervalMs ?? 2000;
  const timeoutMs = options.timeoutMs ?? 30000;
  const start = Date.now();

  const nodeQuery = {
    query: `
      query getMediaImage($id: ID!) {
        node(id: $id) {
          ... on MediaImage {
            fileStatus
            image {
              url
            }
          }
        }
      }
    `,
    variables: { id: mediaImageId },
  };

  for (;;) {
    const response = await adminGraphqlProxy(nodeQuery, { timeout: GRAPHQL_TIMEOUT_MS });
    throwIfGraphqlErrors(response, "Failed to fetch MediaImage status");
    const node = response.data?.node;
    if (!node) {
      throw new Error("MediaImage node not found");
    }
    const { fileStatus } = node;
    const imageUrl = node.image?.url;

    if (fileStatus === "FAILED") {
      throw new Error("Image processing failed.");
    }
    if (fileStatus === "READY" && imageUrl) {
      return imageUrl;
    }
    if (Date.now() - start >= timeoutMs) {
      throw new Error("Image processing timed out; please try again.");
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
}

/**
 * Upload image to Shopify Files (stagedUploadsCreate → POST to staged URL → fileCreate).
 * Uses Node admin GraphQL proxy (session token) — same pattern as Admin_react, no window.shopify.
 *
 * @param {File} file
 * @param {string} [altText]
 * @returns {Promise<{ url: string, mediaImageId: string }>}
 */
export async function uploadImageToShopify(file, altText = "") {
  if (!file || typeof file !== "object") {
    throw new Error("Valid file is required");
  }
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("File must be an image");
  }

  const stagedUploadQuery = {
    query: `
      mutation stagedUploadsCreate($input: [StagedUploadInput!]!) {
        stagedUploadsCreate(input: $input) {
          stagedTargets {
            resourceUrl
            url
            parameters {
              name
              value
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      input: [
        {
          resource: "IMAGE",
          filename: file.name || "upload.jpg",
          mimeType: file.type || "image/jpeg",
          fileSize: String(file.size ?? 0),
          httpMethod: "POST",
        },
      ],
    },
  };

  const stagedResponse = await adminGraphqlProxy(stagedUploadQuery, { timeout: GRAPHQL_TIMEOUT_MS });

  throwIfGraphqlErrors(stagedResponse, "Failed to create staged upload");

  if (stagedResponse.data?.stagedUploadsCreate?.userErrors?.length > 0) {
    const errors = stagedResponse.data.stagedUploadsCreate.userErrors.map((e) => e.message).join(", ");
    throw new Error(`Failed to create staged upload: ${errors}`);
  }

  const stagedTarget = stagedResponse.data?.stagedUploadsCreate?.stagedTargets?.[0];
  if (!stagedTarget?.url || !stagedTarget?.resourceUrl) {
    throw new Error("Failed to get staged upload target");
  }
  if (!Array.isArray(stagedTarget.parameters) || stagedTarget.parameters.length === 0) {
    throw new Error("Staged upload target missing required parameters");
  }

  const formData = new FormData();
  stagedTarget.parameters.forEach((param) => {
    if (!param.name || param.value === undefined || param.value === null) return;
    formData.append(param.name, param.value);
  });
  formData.append("file", file);

  const uploadResponse = await fetch(stagedTarget.url, {
    method: "POST",
    body: formData,
  });

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text();
    throw new Error(`Failed to upload file: ${uploadResponse.status} — ${errorText.slice(0, 200)}`);
  }

  const fileCreateMutation = {
    query: `
      mutation fileCreate($files: [FileCreateInput!]!) {
        fileCreate(files: $files) {
          files {
            ... on MediaImage {
              id
              fileStatus
              image {
                url
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      files: [
        {
          originalSource: stagedTarget.resourceUrl,
          alt: altText || undefined,
        },
      ],
    },
  };

  const fileCreateResponse = await adminGraphqlProxy(fileCreateMutation, { timeout: GRAPHQL_TIMEOUT_MS });

  throwIfGraphqlErrors(fileCreateResponse, "Failed to create file");
  if (fileCreateResponse.data?.fileCreate?.userErrors?.length > 0) {
    const errMsg = fileCreateResponse.data.fileCreate.userErrors.map((e) => e.message).join(", ");
    throw new Error(`Failed to create file: ${errMsg}`);
  }

  const mediaImage = fileCreateResponse.data?.fileCreate?.files?.[0];
  if (!mediaImage?.id) {
    throw new Error("Failed to create permanent file for image.");
  }
  if (mediaImage.fileStatus === "FAILED") {
    throw new Error("Image processing failed.");
  }

  let cdnUrl = mediaImage.image?.url;
  if (!cdnUrl) {
    cdnUrl = await pollMediaImageUrl(mediaImage.id);
  }

  return {
    url: cdnUrl,
    mediaImageId: mediaImage.id,
  };
}
